import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './mockFirebaseConfig';
import './LandingPage.css';

const BASE = 'https://res.cloudinary.com/dstxhvkns/image/upload/f_auto,q_auto,w_1920';

const BACKGROUND_IMAGES = [
  `${BASE}/v1777225856/underwater_2_nebuck.png`,
  `${BASE}/v1777225857/viking_nr3mur.png`,
  `${BASE}/v1777225856/underwater_m6t3nz.png`,
  `${BASE}/v1777225856/steampunk_bdq3rm.png`,
  `${BASE}/v1777225856/space_c6urr1.png`,
  `${BASE}/v1777225856/profsnotes-futuristic2_zhhufi.png`,
  `${BASE}/v1777225856/profsnotes-cyberpunk_ri37hb.png`,
  `${BASE}/v1777225856/profsnotes-zen1_cajydo.png`,
  `${BASE}/v1777225856/renaissance_qhlxf1.png`,
  `${BASE}/v1777225855/polar_station_j7xwtt.png`,
  `${BASE}/v1777225855/jurassic_ajkq8b.png`,
  `${BASE}/v1777225855/joaqu7_Photorealistic_Norse_mead_grand_lecture_hall_camera_vi_610b1dda-7e0a-4c60-b191-a4bf04f470d9_0_kayk1e.png`,
  `${BASE}/v1777225855/paleolithic_s30ien.png`,
  `${BASE}/v1777225855/modern_wjzyro.png`,
  `${BASE}/v1777225855/medieval_myhvtb.png`,
  `${BASE}/v1777225855/jungle_qjmgo5.png`,
  `${BASE}/v1777225854/cyberpunk_lp9fyj.png`,
  `${BASE}/v1777225855/industrial_jqgfx7.png`,
  `${BASE}/v1777225854/futuristic_ai_c1re3j.png`,
  `${BASE}/v1777225854/gatsby_iolwj0.png`,
  `${BASE}/v1777225854/drive_in_em6vp1.png`,
  `${BASE}/v1777225854/atomicpunk_efzjue.png`,
  `${BASE}/v1777225854/bomb_shelter_vtxwwh.png`,
  `${BASE}/v1777225854/feudal_engylj.png`,
  `${BASE}/v1777225854/futuristic_iziikb.png`,
  `${BASE}/v1777225853/apocalytic_qw9ehr.png`,
  `${BASE}/v1777225853/ancient_dyansty2_yzcwj1.png`,
  `${BASE}/v1777225853/ancient_egyptian_lortak.png`,
  `${BASE}/v1777225853/ancient_dynasty_sm8u46.png`,
  `${BASE}/v1777225853/ancient_greek_qyymof.png`,
  `${BASE}/v1777225853/ancient_roman_bgxbat.png`,
];

const TOOLS = [
  { id: 'tutor-mech3202', name: 'AI Tutor -- Fluid Dynamics',  course: 'MECH3202',     href: 'https://mech3202.profanswers.com/login' },
  { id: 'tutor-mech2201', name: 'AI Tutor -- Thermodynamics',  course: 'MECH2201',     href: 'https://mech2201.profanswers.com/login' },
  { id: 'aria',           name: 'ARIA -- Reviewer & Planner',  course: 'Research Tool', href: 'https://aria-reviewer-planner.profanswers.com/login' },
];

const courseTag = (course) => course === 'Research Tool' ? '★' : course.slice(0, 2);

const preloadImage = (url) => {
  const img = new Image();
  img.src = url;
};

const LandingPage = () => {
  const [user, setUser] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [selectedToolId, setSelectedToolId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const selectorRef = useRef(null);
  const navigate = useNavigate();

  const TRANSITION_DURATION = 3000;
  const IMAGE_DISPLAY_DURATION = 5000;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    preloadImage(BACKGROUND_IMAGES[0]);
    preloadImage(BACKGROUND_IMAGES[1]);
    return () => unsubscribe();
  }, []);

  const cycleImage = useCallback(() => {
    const currentElement = document.querySelector('.cycling-background.current');
    const nextElement = document.querySelector('.cycling-background.next');

    if (currentElement && nextElement) {
      currentElement.classList.remove('current');
      currentElement.classList.add('fade-out');
      nextElement.classList.remove('next');
      nextElement.classList.add('current');
    }

    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);

    transitionTimeoutRef.current = setTimeout(() => {
      const newNextIndex = (nextImageIndex + 1) % BACKGROUND_IMAGES.length;
      setCurrentImageIndex(nextImageIndex);
      setNextImageIndex(newNextIndex);
      preloadImage(BACKGROUND_IMAGES[newNextIndex]);
    }, TRANSITION_DURATION);
  }, [nextImageIndex]);

  useEffect(() => {
    if (BACKGROUND_IMAGES.length > 1) {
      const intervalId = setInterval(cycleImage, IMAGE_DISPLAY_DURATION);
      return () => {
        clearInterval(intervalId);
        if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
      };
    }
  }, [cycleImage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (selectorRef.current && !selectorRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setMenuOpen((prev) => !prev);
  };

  const selectTool = (toolId, index) => {
    setSelectedToolId(toolId);
    setActiveIndex(index);
    setMenuOpen(false);
  };

  const handleAccess = () => {
    if (!selectedToolId) return;
    const tool = TOOLS.find((t) => t.id === selectedToolId);
    if (tool) window.location.href = tool.href;
  };

  const handleTriggerKeyDown = (e) => {
    if (!menuOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % TOOLS.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + TOOLS.length) % TOOLS.length);
        break;
      case 'Enter':
        e.preventDefault();
        selectTool(TOOLS[activeIndex].id, activeIndex);
        break;
      case 'Escape':
        setMenuOpen(false);
        break;
      default:
        break;
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('support@profanswers.com');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };

  const selectedTool = TOOLS.find((t) => t.id === selectedToolId);
  const triggerLabel = selectedTool
    ? `${selectedTool.name} · ${selectedTool.course}`
    : user
    ? 'Choose a tool'
    : 'Login to continue';

  return (
    <div className="stage">
      <div
        className="cycling-background current"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGES[currentImageIndex]})` }}
      />
      <div
        className="cycling-background next"
        style={{ backgroundImage: `url(${BACKGROUND_IMAGES[nextImageIndex]})` }}
      />
      <div className="bg-vignette" />

      <div className="brand">
        <img
          src={`${process.env.PUBLIC_URL}/main_front/profanswers-logo.png`}
          alt="Prof Answers AI logo"
        />
        <div className="brand-name">Prof Answers AI</div>
      </div>

      <div className="hero">
        <h1>{"Prof Answers,\nyour educational platform."}</h1>
      </div>

      <div
        className="selector"
        ref={selectorRef}
        data-open={menuOpen ? 'true' : 'false'}
      >
        {menuOpen && (
          <div className="menu" role="listbox" aria-label="Available tools">
            <div className="menu-head">
              <span>Available tools</span>
              <span>{TOOLS.length} live</span>
            </div>
            {TOOLS.map((tool, index) => (
              <div
                key={tool.id}
                className={`item${index === activeIndex ? ' active' : ''}`}
                role="option"
                aria-selected={tool.id === selectedToolId}
                id={`tool-${tool.id}`}
                onClick={() => selectTool(tool.id, index)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className="icon">{courseTag(tool.course)}</div>
                <div className="meta">
                  <div className="meta-title">{tool.name}</div>
                  <div className="meta-sub">{tool.course}</div>
                </div>
                {index === activeIndex && <div className="hint">↵ open</div>}
              </div>
            ))}
          </div>
        )}

        <div className="selector-row">
          <button
            className={`trigger${selectedToolId ? ' has-value' : ''}`}
            onClick={toggleMenu}
            onKeyDown={handleTriggerKeyDown}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
          >
            <span className="trigger-label">{triggerLabel}</span>
            <span className="chev">
              <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden="true">
                <path fill="currentColor" d="M2 4 L6 8 L10 4 Z" />
              </svg>
            </span>
          </button>
          <button
            className="access"
            onClick={handleAccess}
            disabled={!selectedToolId}
          >
            Open →
          </button>
        </div>
      </div>

      <div
        className="help-wrap"
        onMouseEnter={() => setHelpOpen(true)}
        onMouseLeave={() => setHelpOpen(false)}
      >
        {helpOpen && (
          <div className="help-popup">
            <button className="help-option" onClick={handleCopyEmail}>
              {emailCopied ? '✓ Copied!' : 'Copy support email'}
            </button>
            <a className="help-option" href="mailto:support@profanswers.com">
              Send email
            </a>
          </div>
        )}
        <button className="help-btn">Help</button>
      </div>
    </div>
  );
};

export default LandingPage;
